import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Card, CardContent, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { FormInput } from '../../../components/hook-form/form-input.component';
import { DeleteButton } from '../../../components/delete-button.component';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { useFormContext } from 'react-hook-form';
import { Icons } from '../../../assets/icons';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { useRef, useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { FormSelect } from '../../query-builder/hooks-form-querybuilder/form-select.component';
import { getAddressAbbreviationError } from '../../../utils/abbreviations.util';

const lotTypeOption = [
  {
    value: 'TH',
    label: 'Townhome (TH)',
  },
  {
    value: 'SFR',
    label: 'Single-Family Residential (SFR)',
  },
];

const lotTypeCMOption = [
  {
    value: 'CM',
    label: 'Commercial (CM)',
  },
];

interface BuildingOtherFormProps {
  name: string;
  index: number;
  totalItemCount: number;
  onRemove: (index: number) => void;
  onAddClick?: () => void;
  projectID?: string;
  page?: string;
  syncWithDux: any;
  projectTypeValue: any;
  existingData: any;
}

export const BuildingOtherForm = ({
  name,
  index,
  totalItemCount,
  onRemove,
  onAddClick,
  projectID,
  page,
  syncWithDux,
  projectTypeValue,
  existingData,
}: BuildingOtherFormProps) => {
  const { getValues, setValue, watch, setError, clearErrors } = useFormContext();
  const navigate = useNavigate();
  const jobsiteRef = useRef<string | undefined>(getValues(`${name}.jobsite`));
  const [jobNumberWarning, setJobNumberWarning] = useState<string>('');
  const handleUnitNameChange = (value: string) => {
    const initialJobNumber = jobsiteRef.current || getValues(`${name}.jobsite`);

    const newJobNumber = `${initialJobNumber}${value}`.trim();
    setValue(`${name}.jobsite`, newJobNumber);
  };

  const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

  const handleJobnameNameChange = (value: any) => {
    jobsiteRef.current = value;

    if (!value || value?.trim() === '') {
      clearErrors(`${name}.jobsite`);
      return;
    }

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );

    // 2. (Optional) Remove duplicates by unitID
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) => index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );

    const allUnits = uniqueLots?.map((u: any) => normalize(u?.jobsite));

    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      setError(`${name}.jobsite`, {
        type: 'manual',
        message: 'Jobsite already exists.',
      });
    } else {
      clearErrors(`${name}.jobsite`);
    }
  };

  const handleJobNumberChange = (value: any) => {
    if (!value || value?.trim() === '') {
      setJobNumberWarning('');
      return;
    }

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );

    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) => index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );

    const allUnits = uniqueLots?.map((u: any) => normalize(u?.jobNumber));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    setJobNumberWarning(isDuplicate ? 'Job number already exists.' : '');
  };

  const handleAddressChange = (value: any) => {
    //Check Duplicate value
    if (!value || value?.trim() === '') {
      clearErrors(`${name}.address`);
      return;
    }

    const abbrError = getAddressAbbreviationError(value);

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );

    // 2. (Optional) Remove duplicates by unitID
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) => index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );

    const allUnits = uniqueLots?.map((u: any) => normalize(u?.address));

    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (abbrError) {
      setError(`${name}.address`, {
        type: 'manual',
        message: abbrError,
      });
    } else if (isDuplicate) {
      setError(`${name}.address`, {
        type: 'manual',
        message: 'Address already exists.',
      });
    } else {
      clearErrors(`${name}.address`);
    }
  };

  // const isProjectTypeRES = projectTypeValue === "RES";
  const isProjectTypeRES = true;

  const projectSyncWithDUX = watch(`${name}.projectSyncWithDUX`);

  return (
    <Card elevation={0}>
      <CardContent sx={{ p: 0 }} style={{ paddingTop: '8px', paddingBottom: '8px' }}>
        <Grid container spacing={0}>
          <Grid item lg={11} sm={11} xl={11} xs={11}>
            <Grid lg={12} className="d-flex   ">
              <div className="d-flex flexGap">
                <Grid>
                  {index === 0 && <b style={{ marginBottom: '10px', display: 'block' }}>JOBSITE</b>}
                  <FormInput
                    // disabled={true}
                    name={`${name}.jobsite`}
                    label="Jobsite"
                    onChange={(e: any) => handleJobnameNameChange(e)}
                    isCustomError={true}
                    validationQueryRoute={(value: string) =>
                      projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                        projectID,
                        '',
                        getValues(`${name}.unitID`),
                        '',
                        value
                      )
                    }
                  />
                </Grid>
                <Grid>
                  {index === 0 && <b style={{ marginBottom: '10px', display: 'block' }}>OTHER</b>}
                  <FormInput
                    name={`${name}.commonAreaName`}
                    label="Other"
                    onChange={(e: any) => handleUnitNameChange(e)}
                  />
                </Grid>
                <Grid>
                  {index === 0 && <b style={{ marginBottom: '10px', display: 'block' }}>FLOOR</b>}
                  <FormInput name={`${name}.commonAreaFloorNo`} label="Floor No." type="number" />
                </Grid>

                <Grid>
                  {index === 0 && <b style={{ marginBottom: '10px', display: 'block' }}>JOB NUMBER</b>}
                  <FormInput
                    name={`${name}.jobNumber`}
                    label="Builder Job Number"
                    onChange={(e: any) => handleJobNumberChange(e)}
                  />
                  {jobNumberWarning && (
                    <Typography variant="caption" style={{ color: '#e67e22', fontSize: '11px' }}>
                      {jobNumberWarning}
                    </Typography>
                  )}
                </Grid>
                <Grid style={{ minWidth: '170px' }}>
                  {index === 0 && <b style={{ marginBottom: '10px', display: 'block', minWidth: '170px' }}>ADDRESS</b>}
                  <FormInput
                    name={`${name}.address`}
                    label="Address"
                    onChange={(e: any) => handleAddressChange(e)}
                    isCustomError={true}
                    validationQueryRoute={(value: string) =>
                      projectsApiKeyStore.checkDuplicateLotAddress(projectID, value, getValues(`${name}.unitID`))
                    }
                  />
                </Grid>
                <Grid style={{ minWidth: '170px' }}>
                  {index === 0 && <b style={{ marginBottom: '10px', display: 'block' }}>SQUARE FEET</b>}
                  <FormInput name={`${name}.squareFeet`} label={'Square Feet'} />
                </Grid>
                <Grid style={{ minWidth: '170px' }}>
                  {index === 0 && (
                    <b style={{ marginBottom: '10px', display: 'block', minWidth: '150px' }}>PROCTOR RESULT</b>
                  )}
                  {/* <FormAutoCompleteMultiSelectDynamic
                    name={`${name}.proctorResult`}
                    label="Proctor Result"
                    getQueryKeyConfig={() =>
                      proctorResultApiKeyStore.getProjectProctorResults(!projectID ? 0 : Number(projectID))
                    }
                    optionsValueKey="proctorResultsID"
                    optionsLabelKey="proctorFileName"
                  /> */}
                  <FormAutoCompleteSelectDynamic
                    name={`${name}.proctorResult`}
                    label="Proctor Result"
                    getQueryKeyConfig={() =>
                      proctorResultApiKeyStore.getProjectProctorResults(!projectID ? 0 : Number(projectID))
                    }
                    optionsValueKey="proctorResultsID"
                    optionsLabelKey="proctorFileName"
                  />
                </Grid>
                {isProjectTypeRES && (
                  <Grid style={{ minWidth: '130px' }}>
                    {index === 0 && (
                      <b style={{ marginBottom: '10px', display: 'block', minWidth: '130px' }}>Lot Type</b>
                    )}
                    <FormSelect
                      name={`${name}.lotType`}
                      label="Lot Type"
                      options={projectTypeValue === 'RES' ? lotTypeOption : lotTypeCMOption}
                      error={true}
                    />
                  </Grid>
                )}
                <Grid style={{ minWidth: '130px' }}>
                  {index === 0 && (
                    <b style={{ marginBottom: '10px', display: 'block', minWidth: '130px' }}>CREATE WO</b>
                  )}
                  <FormCheckbox label="Create WO" name={`${name}.createWO`} />
                </Grid>
                <Grid style={{ minWidth: '180px' }}>
                  {index === 0 && (
                    <b style={{ marginBottom: '10px', display: 'block', minWidth: '130px' }}>SUNC WITH DUX</b>
                  )}
                  <FormCheckbox label="Sync With DUX" name={`${name}.syncWithDUX`} disabled={!projectSyncWithDUX} />
                </Grid>
                <Grid style={{ minWidth: '180px' }}>
                  {index === 0 && (
                    <b style={{ marginBottom: '10px', display: 'block', minWidth: '100px' }}>BUILDING PERMIT</b>
                  )}
                  <FormInput label="Building Permit#" name={`${name}.buildingPermit`} />
                </Grid>
                <div
                  style={{
                    display: 'flex',
                    height: 'auto',
                    marginTop: 'auto',
                    minHeight: '40px',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    {page && getValues(`${name}.isUnitCreationCompleted`) && (
                      // <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_UNIT]}>
                      <IconButton
                        style={{ padding: 5 }}
                        aria-label="view"
                        onClick={() => navigate(`/units/profile/${getValues(`${name}.unitID`)}`)}
                      >
                        <Tooltip title={<Typography variant="body1">View</Typography>}>
                          <img src={Icons.IconUnitsLotsDetails} alt="View" />
                        </Tooltip>
                      </IconButton>
                      // </GuardedComponent>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    height: 'auto',
                    marginTop: 'auto',
                    minHeight: '40px',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_UNIT]}>
                      {!getValues(`${name}.disableDelete`) && (
                        <DeleteButton
                          data={undefined}
                          deleteMutationConfig={() =>
                            projectsApiKeyStore.deleteCommonArea(getValues(`${name}.commonAreaID`))
                          }
                          onDeleted={() => onRemove(index)}
                        >
                          <>
                            {' '}
                            <img src={Icons.Delete} alt="DELETE" />
                          </>
                        </DeleteButton>
                      )}
                    </GuardedComponent>
                    {/* <IconButton aria-label="delete" onClick={() => onRemove(index)}>
                      <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                        <DeleteIcon className="btnDelete" />
                      </Tooltip>
                    </IconButton> */}
                  </div>
                </div>
              </div>

              {onAddClick && index >= totalItemCount - 1 && (
                <div style={{ display: 'flex', height: 'auto', marginTop: 'auto' }}>
                  <div>
                    {' '}
                    <Button
                      className="btn btn-primary btnIcon"
                      variant="text"
                      sx={{ p: 0, m: 0, mr: 1 }}
                      onClick={onAddClick}
                    >
                      <AddIcon />
                    </Button>{' '}
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
